import { useEffect, useMemo, useRef, useState } from 'react'
import { AttendeeStatusBadge, Button, message } from '@/components/ui'
import { SearchOutlined, PrinterOutlined, QrcodeOutlined, SettingOutlined } from '@ant-design/icons'
import type { BadgePrintEventData, BadgePrintLog, BadgeTemplate } from '@/types/badgeDesigner'
import { normalizeBadgeTemplate, templateIsDoubleSided } from '@/lib/badgeTemplate'
import { api } from '@/lib/apiClient'
import { formatDateTime } from '@/pages/events/attendees/utils/formatters'
import { useBadgePrinterSelection } from '@/hooks/useBadgePrinterSelection'
import PrinterSelector from '@/components/badge_printing/PrinterSelector'
import { renderBadgeSideCanvas } from '@/lib/badgePrint'
import { toPrintRegistrantPayload, triggerRegistrantBadgePrint } from '@/lib/registrantBadgePrint'
import type { AttendeeStatus } from '@/pages/events/attendees/types'

type RegistrantSearchResult = {
  id: number
  full_name?: string | null
  email?: string | null
  phone?: string | null
  qr_value?: string | null
  status: string
}

type RegistrationDetail = {
  identifier: string
  label: string
  value: unknown
}

type RegistrantScanAttendance = {
  id: number
  scan_point_name?: string | null
  scanned_at: string
  scanned_by?: string | null
}

type RegistrantLookupDetail = {
  id: number
  full_name?: string | null
  email?: string | null
  phone?: string | null
  qr_value?: string | null
  status: string
  source: string
  checked_in_at?: string | null
  submitted_at?: string | null
  created_at: string
  answers?: Record<string, unknown>
  registration_details?: RegistrationDetail[]
  badge_print_logs?: BadgePrintLog[]
  has_printed_badge?: boolean
  badge_print_count?: number
  scan_attendances?: RegistrantScanAttendance[]
}

type SearchRegistrantsResponse = {
  results?: RegistrantSearchResult[]
}

type FindRegistrantResponse = {
  registrant?: RegistrantLookupDetail
}

interface BadgeScanPrintTabProps {
  eventSlug: string
  eventData?: BadgePrintEventData
  badgeTemplate?: BadgeTemplate
  isBadgePrintingEnabled?: boolean
  onOpenSettings?: () => void
}

const BADGE_PRINTING_DISABLED_MESSAGE =
  'Badge printing is disabled. Open the Settings tab in Badge Printing and turn on Allow Badge Printing.'

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '-'
  if (Array.isArray(value)) return value.map((item) => String(item)).join(', ')
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

export default function BadgeScanPrintTab({
  eventSlug,
  eventData,
  badgeTemplate,
  isBadgePrintingEnabled = false,
  onOpenSettings,
}: BadgeScanPrintTabProps) {
  const scannerInputRef = useRef<HTMLInputElement | null>(null)
  const manualSearchRef = useRef<HTMLDivElement | null>(null)
  const [scannerInput, setScannerInput] = useState('')
  const [manualQuery, setManualQuery] = useState('')
  const [manualResults, setManualResults] = useState<RegistrantSearchResult[]>([])
  const [selectedRegistrant, setSelectedRegistrant] = useState<RegistrantLookupDetail | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [isLookingUp, setIsLookingUp] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)
  const [isRenderingPreview, setIsRenderingPreview] = useState(false)
  const [previewFrontUrl, setPreviewFrontUrl] = useState<string | null>(null)
  const [previewBackUrl, setPreviewBackUrl] = useState<string | null>(null)

  const normalizedBadgeTemplate = useMemo(
    () => (badgeTemplate ? normalizeBadgeTemplate(badgeTemplate) : null),
    [badgeTemplate],
  )

  const {
    printerOptions,
    selectedPrinterUid,
    selectedPrinter,
    setSelectedPrinterUid,
    isLoadingPrinters,
    detectPrinters,
    hasActivePrinter,
    setupRequired,
    setupMessage,
  } = useBadgePrinterSelection(eventSlug, {
    enabled: true,
    autoDetect: true,
    notifyWhenEmpty: false,
  })

  const focusScannerInput = () => {
    scannerInputRef.current?.focus()
  }

  const fetchRegistrant = async (params: { registrantId?: number; qrValue?: string }) => {
    const search = new URLSearchParams()
    if (params.registrantId) search.set('registrant_id', String(params.registrantId))
    if (params.qrValue) search.set('qr_value', params.qrValue)
    const response = await api.get<FindRegistrantResponse>(
      `/events/${eventSlug}/badge_printings/find_registrant?${search.toString()}`,
    )
    return response.registrant || null
  }

  const handleLookupFromScan = async () => {
    const qrValue = scannerInput.trim()
    if (!qrValue) return

    setIsLookingUp(true)
    try {
      const registrant = await fetchRegistrant({ qrValue })
      if (!registrant) {
        message.warning('Registrant not found')
        return
      }

      setSelectedRegistrant(registrant)
      setManualResults([])
      message.success(`Registrant found: ${registrant.full_name || registrant.email || 'Unknown'}`)
    } catch (error: any) {
      const errorMessage =
        error?.data?.errors?.join(', ') || error?.message || 'Registrant not found'
      message.warning(errorMessage)
    } finally {
      setIsLookingUp(false)
      setScannerInput('')
      focusScannerInput()
    }
  }

  const handleManualSearch = async () => {
    const query = manualQuery.trim()
    if (!query) {
      setManualResults([])
      return
    }

    setIsSearching(true)
    try {
      const response = await api.get<SearchRegistrantsResponse>(
        `/events/${eventSlug}/badge_printings/search_registrants?query=${encodeURIComponent(query)}`,
      )
      const results = response.results || []
      setManualResults(results)
      if (results.length === 0) {
        message.warning('No registrants found')
      }
    } catch (error: any) {
      const errorMessage =
        error?.data?.errors?.join(', ') || error?.message || 'Failed to search registrants'
      message.error(errorMessage)
    } finally {
      setIsSearching(false)
    }
  }

  const handleSelectRegistrant = async (registrantId: number) => {
    setIsLookingUp(true)
    try {
      const registrant = await fetchRegistrant({ registrantId })
      if (!registrant) {
        message.warning('Registrant not found')
        return
      }
      setSelectedRegistrant(registrant)
      setManualResults([])
    } catch (error: any) {
      const errorMessage =
        error?.data?.errors?.join(', ') || error?.message || 'Registrant not found'
      message.warning(errorMessage)
    } finally {
      setIsLookingUp(false)
      focusScannerInput()
    }
  }

  const handlePrintBadge = async () => {
    if (!selectedRegistrant) {
      message.warning('Select a registrant first')
      return
    }
    if (!isBadgePrintingEnabled) {
      message.warning(BADGE_PRINTING_DISABLED_MESSAGE)
      return
    }
    if (!normalizedBadgeTemplate) {
      message.warning('Badge template not configured yet')
      return
    }
    if (!selectedPrinterUid || !hasActivePrinter) {
      message.warning(setupMessage || 'Pair a print station before printing badges')
      return
    }

    const printerName = selectedPrinter?.printerName || selectedPrinter?.label
    setIsPrinting(true)
    try {
      const printResponse = await triggerRegistrantBadgePrint({
        eventSlug,
        registrantId: selectedRegistrant.id,
        printerUid: selectedPrinter?.printerUid || selectedPrinterUid,
        printerId: selectedPrinter?.printerId,
        stationId: selectedPrinter?.stationId,
        printStationPrinterId: selectedPrinter?.printStationPrinterId,
        printStationId: selectedPrinter?.printStationId,
        printerProfileId: selectedPrinter?.printerProfileId,
        printerName,
        template: normalizedBadgeTemplate,
        data: {
          registrant: toPrintRegistrantPayload({
            id: selectedRegistrant.id,
            full_name: selectedRegistrant.full_name,
            email: selectedRegistrant.email,
            phone: selectedRegistrant.phone,
            qr_value: selectedRegistrant.qr_value,
            answers: selectedRegistrant.answers || {},
          }),
          event: eventData,
        },
      })

      const refreshed = await fetchRegistrant({ registrantId: selectedRegistrant.id })
      if (refreshed) {
        setSelectedRegistrant(refreshed)
      }

      if (printResponse.scanAttendanceCreated) {
        message.success('Badge print job queued and attendee check-in recorded')
      } else {
        message.success('Badge print job queued')
      }
    } catch (error: any) {
      message.error(error?.message || 'Failed to queue badge print')
    } finally {
      setIsPrinting(false)
      focusScannerInput()
    }
  }

  const registrationDetails = selectedRegistrant?.registration_details || []
  const printLogs = selectedRegistrant?.badge_print_logs || []
  const scanLogs = selectedRegistrant?.scan_attendances || []
  const selectedPrinterName = selectedPrinter?.label
  const badgeStatus: AttendeeStatus = (
    selectedRegistrant &&
    ['pending', 'approved', 'rejected', 'cancelled'].includes(selectedRegistrant.status)
      ? selectedRegistrant.status
      : 'pending'
  ) as AttendeeStatus
  const registrationDetailsForDisplay = useMemo(() => {
    const isProfilePhotoField = (field: RegistrationDetail) => {
      const identifier = field.identifier.toLowerCase()
      const label = field.label.toLowerCase()
      return (
        identifier === 'profile_photo' ||
        identifier === 'profile_photo_url' ||
        label === 'profile photo'
      )
    }

    const filtered = registrationDetails.filter(
      (field) => field.identifier !== 'email' && !isProfilePhotoField(field),
    )
    const hasPhone = filtered.some(
      (field) => field.identifier === 'phone' || field.identifier === 'contact_number',
    )

    if (!hasPhone && selectedRegistrant?.phone) {
      return [{ identifier: 'phone', label: 'Phone', value: selectedRegistrant.phone }, ...filtered]
    }

    return filtered
  }, [registrationDetails, selectedRegistrant?.phone])
  const previewData = useMemo(() => {
    if (!selectedRegistrant) return null

    return {
      registrant: toPrintRegistrantPayload({
        id: selectedRegistrant.id,
        full_name: selectedRegistrant.full_name,
        email: selectedRegistrant.email,
        phone: selectedRegistrant.phone,
        qr_value: selectedRegistrant.qr_value,
        answers: selectedRegistrant.answers || {},
      }),
      event: eventData,
    }
  }, [selectedRegistrant, eventData])

  useEffect(() => {
    let cancelled = false

    const renderPreview = async () => {
      if (!normalizedBadgeTemplate || !previewData) {
        setPreviewFrontUrl(null)
        setPreviewBackUrl(null)
        return
      }

      setIsRenderingPreview(true)
      try {
        const frontCanvas = await renderBadgeSideCanvas(
          normalizedBadgeTemplate,
          'front',
          previewData,
          {
            dpi: 96,
          },
        )
        if (cancelled) return
        setPreviewFrontUrl(frontCanvas.toDataURL('image/png'))

        if (templateIsDoubleSided(normalizedBadgeTemplate)) {
          const backCanvas = await renderBadgeSideCanvas(
            normalizedBadgeTemplate,
            'back',
            previewData,
            {
              dpi: 96,
            },
          )
          if (cancelled) return
          setPreviewBackUrl(backCanvas.toDataURL('image/png'))
        } else {
          setPreviewBackUrl(null)
        }
      } catch {
        if (cancelled) return
        setPreviewFrontUrl(null)
        setPreviewBackUrl(null)
      } finally {
        if (!cancelled) {
          setIsRenderingPreview(false)
        }
      }
    }

    renderPreview()

    return () => {
      cancelled = true
    }
  }, [normalizedBadgeTemplate, previewData])

  useEffect(() => {
    if (manualResults.length === 0) return

    const onClickOutside = (event: MouseEvent) => {
      const node = manualSearchRef.current
      if (!node) return
      if (!node.contains(event.target as Node)) {
        setManualResults([])
      }
    }

    document.addEventListener('mousedown', onClickOutside)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
    }
  }, [manualResults.length])

  const renderDisabledNotice = () => (
    <div className="rounded border border-amber-200 bg-amber-50 text-amber-800 text-xs px-3 py-2">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <span>{BADGE_PRINTING_DISABLED_MESSAGE}</span>
        {onOpenSettings && (
          <Button
            variant="tertiary"
            size="small"
            icon={<SettingOutlined />}
            className="shrink-0"
            onClick={onOpenSettings}
          >
            Open Badge Printing Settings
          </Button>
        )}
      </div>
    </div>
  )

  return (
    <div className="space-y-4">
      <div className="grid gap-4 xl:grid-cols-3">
        <div className="bg-white border border-gray-200 rounded-md p-4 space-y-3">
          <div>
            <h3 className="text-base font-semibold m-0">Scanner Input</h3>
            <p className="text-xs text-gray-500 mt-1 mb-0">
              Connect QR scanner. Keep this input focus before scan.
            </p>
          </div>

          <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
            <input
              ref={scannerInputRef}
              value={scannerInput}
              onChange={(event) => setScannerInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  handleLookupFromScan()
                }
              }}
              autoFocus
              className="h-9 rounded border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
              placeholder="Scan QR value here"
            />
            <Button variant="tertiary" icon={<QrcodeOutlined />} onClick={focusScannerInput}>
              Focus
            </Button>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-md p-4 space-y-3">
          <div>
            <h3 className="text-base font-semibold m-0">Manual Search</h3>
            <p className="text-xs text-gray-500 mt-1 mb-0">
              Search by name, email, phone or QR value.
            </p>
          </div>

          <div ref={manualSearchRef} className="relative">
            <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
              <input
                value={manualQuery}
                onChange={(event) => setManualQuery(event.target.value)}
                onFocus={() => {
                  if (manualResults.length > 0) return
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    handleManualSearch()
                  }
                  if (event.key === 'Escape') {
                    setManualResults([])
                  }
                }}
                className="h-9 rounded border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="Search registrant"
              />
              <Button
                variant="primary"
                icon={<SearchOutlined />}
                onClick={handleManualSearch}
                loading={isSearching}
              >
                Search
              </Button>
            </div>

            {manualResults.length > 0 && (
              <div className="absolute z-30 top-full left-0 right-0 mt-1 max-h-56 overflow-auto rounded border border-gray-200 bg-white shadow-lg">
                {manualResults.map((result) => (
                  <button
                    key={result.id}
                    type="button"
                    onClick={() => handleSelectRegistrant(result.id)}
                    className="w-full text-left px-3 py-2 border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
                  >
                    <div className="text-sm font-medium text-gray-900">
                      {result.full_name || result.email || `Registrant #${result.id}`}
                    </div>
                    <div className="text-xs text-gray-500">
                      {result.email || '-'} {result.phone ? `• ${result.phone}` : ''}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-md p-4 space-y-3">
          <div>
            <h3 className="text-base font-semibold m-0">Print Station</h3>
            <p className="text-xs text-gray-500 mt-1 mb-0">
              Agent printer: {selectedPrinterName || 'None selected'}
            </p>
          </div>

          <PrinterSelector
            selectedPrinterUid={selectedPrinterUid}
            printerOptions={printerOptions}
            isLoadingPrinters={isLoadingPrinters}
            onPrinterChange={(value) => setSelectedPrinterUid(value)}
            onRefresh={() => detectPrinters({ notifyOnSuccess: true, notifyWhenEmpty: true })}
            setupRequired={setupRequired}
            setupMessage={setupMessage}
          />
        </div>
      </div>

      {!isBadgePrintingEnabled && renderDisabledNotice()}

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="bg-white border border-gray-200 rounded-md p-4">
          {!selectedRegistrant ? (
            <div className="h-full min-h-[240px] flex items-center justify-center text-sm text-gray-500">
              Scan or search a registrant to view details.
            </div>
          ) : (
            <div className="space-y-5">
              <div className="border-b border-gray-100 pb-4">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-semibold m-0">
                    {selectedRegistrant.full_name || selectedRegistrant.email || 'Registrant'}
                  </h3>
                  <AttendeeStatusBadge status={badgeStatus} />
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <div>Email: {selectedRegistrant.email || '-'}</div>
                  <div>QR Value: {selectedRegistrant.qr_value || '-'}</div>
                </div>
              </div>

              <div className="border-b border-gray-100 pb-4">
                <h4 className="text-sm font-semibold mb-2">Registration Details</h4>
                {registrationDetailsForDisplay.length === 0 ? (
                  <p className="text-xs text-gray-500">No registration details available.</p>
                ) : (
                  <div className="space-y-2">
                    {registrationDetailsForDisplay.map((field) => (
                      <div
                        key={field.identifier}
                        className="flex items-start justify-between gap-4"
                      >
                        <div className="text-xs text-gray-500">{field.label}</div>
                        <div className="text-sm font-medium break-words text-right">
                          {formatValue(field.value)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-semibold mb-2">Badge Print Jobs</h4>
                  {printLogs.length === 0 ? (
                    <p className="text-xs text-gray-500">No badge print jobs yet.</p>
                  ) : (
                    <div className="space-y-2 max-h-56 overflow-auto">
                      {printLogs.map((log) => (
                        <div
                          key={log.id}
                          className="rounded border border-gray-100 bg-white px-3 py-2"
                        >
                          <div className="text-xs font-medium text-gray-900">
                            {log.printer_name || 'Unknown printer'}
                          </div>
                          <div className="text-[11px] text-gray-500">
                            {log.status} • Queued by {log.triggered_by || 'System'} •{' '}
                            {formatDateTime(log.printed_at)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="text-sm font-semibold mb-2">Scan Attendance Logs</h4>
                  {scanLogs.length === 0 ? (
                    <p className="text-xs text-gray-500">No scan attendance logs yet.</p>
                  ) : (
                    <div className="space-y-2 max-h-56 overflow-auto">
                      {scanLogs.map((scan) => (
                        <div
                          key={scan.id}
                          className="rounded border border-gray-100 bg-white px-3 py-2"
                        >
                          <div className="text-xs font-medium text-gray-900">
                            {scan.scan_point_name || 'Scan Point'}
                          </div>
                          <div className="text-[11px] text-gray-500">
                            {scan.scanned_by ? `Scanned by ${scan.scanned_by} • ` : ''}
                            {formatDateTime(scan.scanned_at)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-md p-4 space-y-4 h-fit">
          <div>
            <h3 className="text-base font-semibold m-0">Queue Badge Print</h3>
            <p className="text-xs text-gray-500 mt-1 mb-0">
              Create a pending print job for the active print station.
            </p>
          </div>

          {!isBadgePrintingEnabled && renderDisabledNotice()}

          {!normalizedBadgeTemplate && (
            <div className="rounded border border-amber-200 bg-amber-50 text-amber-700 text-xs px-3 py-2">
              Badge template is not configured.
            </div>
          )}

          <Button
            block
            variant="primary"
            icon={<PrinterOutlined />}
            loading={isPrinting}
            onClick={handlePrintBadge}
            disabled={
              !selectedRegistrant ||
              !normalizedBadgeTemplate ||
              !isBadgePrintingEnabled ||
              !selectedPrinterUid ||
              !hasActivePrinter ||
              setupRequired ||
              isLookingUp
            }
          >
            Queue Print Job
          </Button>

          <div className="pt-3 border-t border-gray-100 space-y-3">
            <div>
              <h3 className="text-base font-semibold m-0">Badge Preview</h3>
              <p className="text-xs text-gray-500 mt-1 mb-0">
                Live preview for the selected registrant.
              </p>
            </div>

            {!selectedRegistrant ? (
              <p className="text-xs text-gray-500">Select a registrant to preview their badge.</p>
            ) : !normalizedBadgeTemplate ? (
              <p className="text-xs text-gray-500">Badge template is not configured.</p>
            ) : isRenderingPreview ? (
              <p className="text-xs text-gray-500">Rendering preview...</p>
            ) : previewFrontUrl ? (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[11px] text-gray-500 mb-1">Front</div>
                  <img
                    src={previewFrontUrl}
                    alt="Badge front preview"
                    className="w-full max-w-[220px] border border-gray-200 rounded bg-white"
                  />
                </div>
                {previewBackUrl && (
                  <div>
                    <div className="text-[11px] text-gray-500 mb-1">Back</div>
                    <img
                      src={previewBackUrl}
                      alt="Badge back preview"
                      className="w-full max-w-[220px] border border-gray-200 rounded bg-white"
                    />
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-500">Unable to render badge preview.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
